import { LocalStorageKeyEnum } from '@shared-enums/local-storage.enum';
import { PayrollTypeBaseEnum, PayrollTypeFullEnum } from '@shared-enums/time.enum';
import { checkLocalStorage, updateLocalStorage } from '@shared-helpers/storage-helpers';
import { IHttpResponseNonBill } from '@shared-interfaces/api';

import { ISelectOptions } from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';

export class NonbillableCodes {
  public selectOptions: ISelectOptions[] = [];
  public absence: ISelectOptions[] = [];
  public attendance: ISelectOptions[] = [];

  private codes: IHttpResponseNonBill[] = [];

  constructor(datahub: DatahubService) {
    const cached = checkLocalStorage<IHttpResponseNonBill[]>(LocalStorageKeyEnum.NonBill);
    if (cached) this.init(cached);
    datahub.apiService.getNonbillable$().subscribe({
      next: (codes) => {
        if (!cached) this.init(codes);
        updateLocalStorage(LocalStorageKeyEnum.NonBill, codes);
      },
      error: (err) => {
        console.error('Error fetching nonbillable codes:', err);
      },
    });
  }

  public getType(absAttCode: string): PayrollTypeFullEnum {
    const found = this.fetchOrFail(absAttCode);
    if (!found.Paid) return PayrollTypeFullEnum.Unpaid;
    if (found.Holiday) {
      return PayrollTypeFullEnum.Straight;
    } //convert this to 'PayrollTypeEnum.Holiday' when they want holiday back
    //NOTE: We can remove the lowercase conversion when we are fully using the Nest server since it will send us the data in the format we need/expect!
    else return found.Type.toLowerCase() as PayrollTypeFullEnum;
  }

  public getSOD(absAttCode: string): PayrollTypeBaseEnum {
    const found = this.fetchOrFail(absAttCode);
    if (!found.Paid) return PayrollTypeBaseEnum.Straight;
    //This feels wrong! We get one enum type back but we are forcing it to a more narrow enum type here! This should stay consistent
    //NOTE: We can remove the lowercase conversion when we are fully using the Nest server since it will send us the data in the format we need/expect!
    return found.Type.toLowerCase() as PayrollTypeBaseEnum;
  }

  public getDescription(code: string): string {
    const found = this.fetchOrFail(code);
    return found.Description;
  }

  private init(codes: IHttpResponseNonBill[]): void {
    this.codes = codes;
    this.selectOptions = this.makeSelectOptions();
    this.makeAbsenceAttendanceArrays();
  }

  private fetchOrFail(absAttCode: string): IHttpResponseNonBill {
    const found = this.codes.find((x) => x.Code === absAttCode);
    if (found) return found;
    throw new Error(
      `The app asked for information about nonbillable code ${absAttCode}, but the code is not in the nonbillable service's memory. Likely, the solution is to add the code to ZNONBILLATYPES table, but possibly this is an application error.`,
    );
  }

  private makeAbsenceAttendanceArrays(): void {
    this.absence = this.selectOptions.filter((oo) => !!oo.absence);
    this.attendance = this.selectOptions.filter((oo) => !oo.absence);
  }

  private makeSelectOptions(): ISelectOptions[] {
    const options = this.codes.map((code) => {
      return {
        value: code.Code,
        description: code.Description,
        display: code.Description,
        absence: code.Absence,
      };
    });

    // return this.selectOptions but sorted alphabetically by description.
    return options.sort((a, b) => {
      const descriptionA = a.description.toUpperCase();
      const descriptionB = b.description.toUpperCase();
      if (descriptionA > descriptionB) {
        return 1;
      }
      if (descriptionA < descriptionB) {
        return -1;
      }
      return 0;
    });
  }
}
